/**
 * CR-5880: KPI graph milestones — dashed vertical lines with a label above the
 * plot, on the OTIF and OTD graphs.
 *
 * The dashboard payload places each milestone on the x-axis value (`period`) it
 * falls in. This module keeps the ones for the graph's metric that are on the
 * graph, merges milestones sharing a period into one line (a name once), and staggers labels
 * that would otherwise collide (marks one or two periods apart alternate
 * between two label rows).
 *
 * Recharts only renders reference lines that are DIRECT children of the chart,
 * so {@link milestoneLines} returns elements rather than being a component.
 */
import { ReferenceLine } from 'recharts';
import type { ChartMilestone } from '../../lib/api/milestones';

/** One line on a graph. */
export interface MilestoneMark {
  /** The x-axis value the line sits on. */
  period: string;
  /** Title(s) shown above the line. */
  label: string;
  /** 0 = label just above the plot, 1 = the row above that. */
  row: 0 | 1;
}

export const MILESTONE_COLOUR = '#374151';
const ROW_HEIGHT = 13;
/** Marks this close (in periods) to the previous one swap label rows. */
const CROWDED_GAP = 2;

/**
 * The lines to draw on one graph.
 *
 * @param milestones - the payload's `charts.milestones`
 * @param metric - which graph: 'otif' or 'otd'
 * @param periods - the graph's x-axis values, in order
 * @returns one mark per period that has a milestone, left to right
 */
export function milestoneMarks(
  milestones: ChartMilestone[] | undefined,
  metric: 'otif' | 'otd',
  periods: string[],
): MilestoneMark[] {
  const titles = new Map<string, string[]>();
  for (const m of milestones ?? []) {
    if (m.applies_to !== 'both' && m.applies_to !== metric) continue;
    if (!periods.includes(m.period)) continue;
    const names = titles.get(m.period) ?? [];
    // Sites of one group can each hold the same milestone; say it once.
    if (!names.includes(m.title)) titles.set(m.period, [...names, m.title]);
  }
  const marks: MilestoneMark[] = [];
  let prevIndex = -Infinity;
  let prevRow: 0 | 1 = 1;
  periods.forEach((period, index) => {
    const names = titles.get(period);
    if (!names) return;
    const row: 0 | 1 = index - prevIndex <= CROWDED_GAP && prevRow === 0 ? 1 : 0;
    marks.push({ period, label: names.join(' · '), row });
    prevIndex = index;
    prevRow = row;
  });
  return marks;
}

/**
 * Top margin that leaves room for the labels above the plot.
 *
 * @param marks - the graph's marks
 * @returns the chart margin
 */
export function milestoneMargin(marks: MilestoneMark[]): { top: number; right: number; bottom: number; left: number } {
  const rows = marks.some((m) => m.row === 1) ? 2 : marks.length ? 1 : 0;
  return { top: 5 + rows * ROW_HEIGHT + (rows ? 4 : 0), right: 5, bottom: 5, left: 5 };
}

interface LabelProps {
  viewBox?: { x?: number; y?: number };
}

/**
 * The dashed lines, as direct chart children.
 *
 * @param marks - from {@link milestoneMarks}
 * @param yAxisId - the graph's left (percentage) axis id
 * @returns `ReferenceLine` elements
 */
export function milestoneLines(marks: MilestoneMark[], yAxisId: string | number) {
  return marks.map((mark) => (
    <ReferenceLine
      key={`milestone-${mark.period}`}
      x={mark.period}
      yAxisId={yAxisId}
      stroke={MILESTONE_COLOUR}
      strokeWidth={1.5}
      strokeDasharray="4 4"
      label={({ viewBox }: LabelProps) => (
        <text
          x={viewBox?.x ?? 0}
          y={(viewBox?.y ?? 0) - 5 - mark.row * ROW_HEIGHT}
          textAnchor="middle"
          fontSize={11}
          fill={MILESTONE_COLOUR}
          data-testid="milestone-label"
        >
          {mark.label}
        </text>
      )}
    />
  ));
}
